import { Camera, type ImageOptions, type Photo } from "@capacitor/camera";
import { reportCustomKey, reportException, reportLog } from "app/core/crashlytics/crashlytics-reporter";
import { CrashlyticsService } from "app/core/crashlytics/crashlytics.service";

/**
 * Native capture watchdog.
 *
 * Launching the system camera puts the app in the background. On memory
 * constrained devices Android may terminate the process to give the camera
 * app room, and the user experiences this as "the app closed by itself".
 *
 * Crucially that is NOT a crash: no exception is ever thrown, so Crashlytics
 * has nothing to report and the problem stays invisible.
 *
 * This module makes it visible. A marker is written to localStorage (which
 * survives process death) before the camera launches and cleared when it
 * returns. On the next startup a surviving marker means the app never came
 * back from the camera, and `didCrashOnPreviousExecution()` separates the two
 * possible reasons:
 *
 *   crashed === true   -> a real crash; Crashlytics already has the report
 *   crashed === false  -> the OS killed the process; reported as a synthetic
 *                         non-fatal so it becomes countable in the console
 *
 * See docs/firebase-crashlytics.md
 */
const PENDING_CAPTURE_KEY = "pms.nativeCapture.pending";

interface PendingCapture {
  operation: string;
  startedAt: number;
}

/**
 * Mirrors isCameraCancellation() in kyc-capture.util.ts. Duplicated rather than imported so that core/ does not depend on modules/.
 */
function isCancellation(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";

  return message.toLowerCase().includes("cancel");
}

function markStarted(operation: string): void {
  try {
    const pending: PendingCapture = { operation, startedAt: Date.now() };
    localStorage.setItem(PENDING_CAPTURE_KEY, JSON.stringify(pending));
  } catch {
    // localStorage can throw (private mode, quota). 
    // Losing the marker only costs diagnostics, so it must never interrupt the capture.
  }
}

function markFinished(): void {
  try {
    localStorage.removeItem(PENDING_CAPTURE_KEY);
  } catch {
    // As above.
  }
}

function readAndClearPending(): PendingCapture | null {
  try {
    const raw = localStorage.getItem(PENDING_CAPTURE_KEY);

    if (!raw) {
      return null;
    }

    localStorage.removeItem(PENDING_CAPTURE_KEY);

    const parsed = JSON.parse(raw) as PendingCapture;

    return typeof parsed?.operation === "string" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Drop-in replacement for `Camera.getPhoto()`.
 *
 * Behaviour is identical -- the same Photo resolves, and the same error
 * (including user cancellation) rejects. Everything added here is diagnostic
 * and cannot alter the outcome.
 *
 * @param options   camera options, passed straight through
 * @param operation short label identifying the call site, e.g. "aadhaar-back"
 */
export async function captureNativePhoto(options: ImageOptions, operation: string): Promise<Photo> {
  markStarted(operation);
  reportLog(`Native capture started: ${operation}`);

  try {
    const photo = await Camera.getPhoto(options);

    reportLog(`Native capture returned: ${operation}`);

    return photo;
  } catch (error) {
    // Cancellation is a normal user action, not a defect worth reporting.
    if (!isCancellation(error)) {
      reportException(error, `Native capture failed (${operation})`);
    }

    throw error;
  } finally {
    // Runs on success, failure and cancellation alike, so the marker can
    // never be left behind by a completed capture.
    markFinished();
  }
}

/**
 * Startup check. Called once from provideCrashlytics() after init().
 */
export async function reportInterruptedCapture(crashlytics: CrashlyticsService): Promise<void> {
  const pending = readAndClearPending();

  if (!pending) {
    return;
  }

  const ageSeconds = Math.round((Date.now() - pending.startedAt) / 1000);
  const crashed = await crashlytics.didCrashOnPreviousExecution();

  reportCustomKey("interrupted_capture_operation", pending.operation);
  reportCustomKey("interrupted_capture_age_seconds", String(ageSeconds));

  if (crashed) {
    // A real crash already produced a full report with a stack trace. Adding a
    // synthetic one would double count the same event, so only leave context.
    reportLog(`Previous run crashed during native capture: ${pending.operation} (${ageSeconds}s)`);

    return;
  }

  // Returned from the camera into a fresh process with no crash on record:
  // the OS reclaimed us. This is the case Crashlytics cannot see by itself.
  reportException(new Error(`Native capture interrupted by process death: ${pending.operation}`), "Capture watchdog");
}
